/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useRef, useState } from "react";

/* Loose, forward-compatible shapes: real definitions carry many optional,
 * renderer-specific keys (panelBackground, titleTemplate, matrix, ...) that
 * the designer must round-trip untouched, so everything keeps an index
 * signature and edits are always shallow merges. */
export interface SLayout { x: number; y: number; w: number; h: number; z?: number }
export interface SQuery { dimensions?: string[]; measures?: string[]; filters?: any[]; sort?: Array<{ field: string; direction?: "asc" | "desc" }>; limit?: number; seriesField?: string | null; [k: string]: any }
export interface SWidget { id: string; type: string; title?: string | null; layout: SLayout; datasetId?: string | null; query?: SQuery; display?: Record<string, any>; [k: string]: any }
export interface SPage { id: string; name: string; order?: number; section?: string | null; canvas?: { width: number; height: number; columns: number; rowHeight: number; gap: number; [k: string]: any }; widgets: SWidget[]; [k: string]: any }
export interface SDef { name: string; pages: SPage[]; defaultPageId: string; theme?: string; globalFilters?: any[]; [k: string]: any }

export const GRID = { columns: 24, rows: 33, rowHeight: 20, gap: 8, width: 1848, height: 944 };

export function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Same rule as preview.html / the TV app: filter-type widgets (and
 * toolbarAction widgets) render in the top filter strip, not in the grid. */
export function isBarWidget(w: SWidget): boolean {
  if (w.display?.keepInCanvas) return false;
  return w.type === "filter" || w.type === "date_filter" || w.type === "text_filter" || w.toolbarAction === true;
}

export function rowsFor(page: SPage): number {
  const c = page.canvas;
  if (!c) return GRID.rows;
  return Math.floor((c.height + c.gap) / (c.rowHeight + c.gap));
}

function overlaps(a: SLayout, b: SLayout): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** First free spot (top-left scan) for a w x h visual; below everything if the page is full. */
export function findFreeSpot(page: SPage, w: number, h: number): SLayout {
  const cols = page.canvas?.columns ?? GRID.columns;
  const rows = rowsFor(page);
  const taken = page.widgets.filter((x) => !isBarWidget(x)).map((x) => x.layout);
  for (let y = 0; y + h <= rows; y++) {
    for (let x = 0; x + w <= cols; x++) {
      const cand = { x, y, w, h };
      if (!taken.some((t) => overlaps(cand, t))) return cand;
    }
  }
  // Page is full: place it on-screen (overlapping, selected) rather than
  // below the visible TV area where nobody would see it.
  return { x: Math.max(0, Math.floor((cols - w) / 2)), y: Math.max(0, Math.floor((rows - h) / 2)), w: Math.min(w, cols), h: Math.min(h, rows) };
}

export function pageIsFull(page: SPage, w: number, h: number): boolean {
  const cols = page.canvas?.columns ?? GRID.columns;
  const rows = rowsFor(page);
  const taken = page.widgets.filter((x) => !isBarWidget(x)).map((x) => x.layout);
  for (let y = 0; y + h <= rows; y++) for (let x = 0; x + w <= cols; x++) if (!taken.some((t) => overlaps({ x, y, w, h }, t))) return false;
  return true;
}

export function collides(page: SPage, id: string, l: SLayout): boolean {
  return page.widgets.some((w) => w.id !== id && !isBarWidget(w) && overlaps(l, w.layout));
}

export function newWidget(type: string, title: string, layout: SLayout): SWidget {
  return {
    id: uid("w"),
    type,
    title,
    layout: { ...layout, z: 1 },
    datasetId: null,
    query: { dimensions: [], measures: [], filters: [], sort: [], limit: 100 },
    // New column/bar/combo visuals opt into the v2.1 chart engine (real Y axis,
    // gridlines, label positions) -- the renderer keeps old reports on the
    // legacy engine unless an axis/label key is present.
    display: {
      showTitle: true,
      showBorder: true,
      showLastRefresh: true,
      ...(type === "bar_chart" || type === "combo_chart" ? { chart: { yAxis: { show: true, gridlines: true } } } : {}),
    },
    interaction: { crossFilter: false, drillDown: false, drillTarget: null },
    refreshSeconds: 60,
    fallback: { showLastSuccessfulData: true, message: "Live source unavailable" },
  };
}

/** Undo/redo with coalescing: consecutive commits sharing a `key` within
 * 900 ms (e.g. typing a title) replace the top entry instead of stacking. */
export function useHistory(initial: SDef | null) {
  const [state, setState] = useState<{ stack: SDef[]; ptr: number }>({ stack: initial ? [initial] : [], ptr: 0 });
  const last = useRef<{ key: string | null; at: number }>({ key: null, at: 0 });

  const commit = useCallback((next: SDef, key?: string) => {
    const now = Date.now();
    const coalesce = !!key && last.current.key === key && now - last.current.at < 900;
    last.current = { key: key ?? null, at: now };
    setState((s) => {
      const base = s.stack.slice(0, s.ptr + 1);
      if (coalesce && base.length > 1) base[base.length - 1] = next;
      else base.push(next);
      const trimmed = base.length > 80 ? base.slice(base.length - 80) : base;
      return { stack: trimmed, ptr: trimmed.length - 1 };
    });
  }, []);
  const undo = useCallback(() => setState((s) => ({ ...s, ptr: Math.max(0, s.ptr - 1) })), []);
  const redo = useCallback(() => setState((s) => ({ ...s, ptr: Math.min(s.stack.length - 1, s.ptr + 1) })), []);
  const reset = useCallback((d: SDef) => setState({ stack: [d], ptr: 0 }), []);
  /** Re-express every undo/redo state on top of a newer server draft (see merge.ts),
   * so undoing never silently drops other people's work. */
  const rebase = useCallback((fn: (d: SDef) => SDef) => setState((s) => ({ ...s, stack: s.stack.map(fn) })), []);

  return {
    definition: state.stack[state.ptr] ?? null,
    commit,
    undo,
    redo,
    reset,
    rebase,
    canUndo: state.ptr > 0,
    canRedo: state.ptr < state.stack.length - 1,
  };
}
